import React, { useRef, useEffect, useState } from "react";
import { TagIcon } from "./SvgIcons.jsx";
import { CSS_CLASSES, INLINE_STYLES, PLACEHOLDERS } from "../utils/cardBodyConstants.js";
import { isEntryUnlocked } from "../utils/cardBodyDateHelpers.js";

/**
 * TagDropdownSection Component
 * Reusable component for tag dropdown in edit and view modes
 */
const TagDropdownSection = ({
  // Mode props
  isEditMode,
  isMergedMode,
  isRowMode = false,
  
  // Tag data
  tagEdit,
  tagIdEdit,
  tagQuery,
  tag, // For view mode - can be group.tag or row.tag
  
  // Dropdown state
  tagEditDropdownOpen,
  setTagEditDropdownOpen,
  setProjectEditDropdownOpen,
  
  // Handlers
  handleTagInputChange,
  createTagSelectHandler,
  createTagDropdownToggleHandler,
  
  // Data
  filteredTagItems,
  
  // Setters
  setTagEdit,
  setTagIdEdit,
  setTagQuery,
  
  // Refs
  tagDropdownContentRef,
  
  // Additional props
  group,
  row,
  className = "",
  style = {},
  onClick,
  onSectionClick,
  getLockHoursHandler,
  globalConfig,
  setClickId,
  initializeEditStateFromGroup,
  effectiveSetEditedId,
  setTaskNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setSprintNameEdit,
  setSprintIdEdit,
  setDateEdit,
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeTemp,
  setEndTimeTemp,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setUFormatedTime,
  setUDuration,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  toggleGroup,
  groupIndex,
  handleOpenPopup,
}) => {
  const currentTag = isEditMode ? (tagEdit || (isRowMode ? row?.tag : group?.tag)?.tag_name) : (isRowMode ? row?.tag : group?.tag);
  const tagName = isEditMode ? tagEdit : (currentTag?.tag_name || currentTag);
  
  // Refs for checking text overflow
  const editModeTagRef = useRef(null);
  const viewModeTagRef = useRef(null);
  const [editModeOverflow, setEditModeOverflow] = useState(false);
  const [viewModeOverflow, setViewModeOverflow] = useState(false);
  
  // Check if tag name is overflowing in edit mode
  useEffect(() => {
    if (editModeTagRef.current && isEditMode && !isMergedMode) {
      const isTextOverflowing = editModeTagRef.current.scrollWidth > editModeTagRef.current.clientWidth;
      setEditModeOverflow(isTextOverflowing);
    } else {
      setEditModeOverflow(false);
    }
  }, [tagEdit, currentTag, tagName, isEditMode, isMergedMode]);
  
  // Check if tag name is overflowing in view/merged mode
  useEffect(() => {
    if (viewModeTagRef.current && (!isEditMode || isMergedMode)) {
      const isTextOverflowing = viewModeTagRef.current.scrollWidth > viewModeTagRef.current.clientWidth;
      setViewModeOverflow(isTextOverflowing);
    } else {
      setViewModeOverflow(false);
    }
  }, [group?.tag, row?.tag, isEditMode, isMergedMode]);
  
  // Handle tag toggle for edit mode
  const handleTagToggle = (e) => {
    if (tagDropdownContentRef?.current && tagDropdownContentRef.current.contains(e.target)) {
      return;
    }
    if (createTagDropdownToggleHandler) {
      createTagDropdownToggleHandler({
        tagDropdownContentRef,
        tagEditDropdownOpen,
        setTagEditDropdownOpen,
        setProjectEditDropdownOpen,
      })();
    } else {
      setTagEditDropdownOpen(!tagEditDropdownOpen);
      if (setProjectEditDropdownOpen) {
        setProjectEditDropdownOpen(false);
      }
    }
  };

  // Handle section click for view/merged modes
  const handleSectionClick = () => {
    if (onSectionClick) {
      onSectionClick();
      return;
    }
    
    if (isRowMode && row) {
      const isUnlocked = isEntryUnlocked(row.endTime, globalConfig, getLockHoursHandler);
      if (isUnlocked) {
        if (initializeEditStateFromGroup) {
          initializeEditStateFromGroup({
            group: row,
            effectiveSetEditedId,
            setProjectEditDropdownOpen,
            setTagEditDropdownOpen,
            setTaskNameEdit,
            setProjectIdEdit,
            setProjectNameEdit,
            setSprintNameEdit,
            setSprintIdEdit,
            setDateEdit,
            setTagIdEdit,
            setTagEdit,
            setTagQuery,
            setStartTimeEdit,
            setEndTimeEdit,
            setStartTimeTemp,
            setEndTimeTemp,
            setStartTimeEdit24,
            setEndTimeEdit24,
            setClientEdit,
            setClientIdEdit,
            setBillableEdit,
            setUFormatedTime,
            setUDuration,
            setUStartTimeError,
            setUEndTimeError,
            setUFormatedTimeError,
            globalConfig,
          });
        }
      } else {
        if (handleOpenPopup) {
          handleOpenPopup();
        }
        if (setClickId && row) {
          setClickId(row._id);
        }
      }
      return;
    }
    
    if (group) {
      const isUnlocked = isEntryUnlocked(group.endTime, globalConfig, getLockHoursHandler);
      
      if (isUnlocked) {
        // For merged groups (entries.length > 1), toggle to expand/collapse
        // For single entry groups (entries.length === 1), enter edit mode
        if (group.entries.length === 1) {
          if (initializeEditStateFromGroup) {
            initializeEditStateFromGroup({
              group,
              effectiveSetEditedId,
              setProjectEditDropdownOpen,
              setTagEditDropdownOpen,
              setTaskNameEdit,
              setProjectIdEdit,
              setProjectNameEdit,
              setSprintNameEdit,
              setSprintIdEdit,
              setDateEdit,
              setTagIdEdit,
              setTagEdit,
              setTagQuery,
              setStartTimeEdit,
              setEndTimeEdit,
              setStartTimeTemp,
              setEndTimeTemp,
              setStartTimeEdit24,
              setEndTimeEdit24,
              setClientEdit,
              setClientIdEdit,
              setBillableEdit,
              setUFormatedTime,
              setUDuration,
              setUStartTimeError,
              setUEndTimeError,
              setUFormatedTimeError,
              globalConfig,
              useMomentFormat: false,
            });
          }
        } else {
          // Toggle group for merged entries (consistent with project-sprint, task, time sections)
          if (toggleGroup && groupIndex !== undefined) {
            toggleGroup(groupIndex);
          }
        }
      } else {
        // Entry is locked
        if (group.entries.length === 1) {
          if (setClickId) {
            setClickId(group._id);
          }
        } else {
          // For merged groups, still toggle even if locked (consistent with other sections)
          if (toggleGroup && groupIndex !== undefined) {
            toggleGroup(groupIndex);
          }
          if (setClickId) {
            setClickId(null);
          }
        }
      }
    }
  };

  // Render edit mode
  if (isEditMode && !isMergedMode) {
    return (
      <div className={`tag-section-overview-outer ${className}`} style={style}>
        <div className="tag-section">
          <p
            className={`${CSS_CLASSES.CURSOR} ${currentTag ? CSS_CLASSES.TAG_AVAILABLE : CSS_CLASSES.TAG_NOT_AVAILABLE}`}
            onClick={handleTagToggle}
          >
            {editModeOverflow && (
              <span className="tooltip-tracker">
                {" "}
                {tagEdit ? (
                  tagEdit
                ) : currentTag ? (
                  typeof currentTag === 'string' ? currentTag : currentTag.tag_name
                ) : (
                  <TagIcon />
                )}
              </span>
            )}
            <span
              ref={editModeTagRef}
              className="tag-edit-tag-name"
              style={INLINE_STYLES.TAG_ELLIPSIS}
            >
              {" "}
              {tagEdit ? (
                tagEdit
              ) : currentTag ? (
                typeof currentTag === 'string' ? currentTag : currentTag.tag_name
              ) : (
                <TagIcon />
              )}
            </span>
          </p>
          {tagEditDropdownOpen && (
            <div className="custom-dropdown" ref={tagDropdownContentRef} onMouseDown={(e) => e.stopPropagation()}>
              <input
                type="text"
                className="search-bar"
                placeholder={PLACEHOLDERS.SEARCH}
                value={tagQuery || ''}
                onChange={handleTagInputChange}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                autoComplete="off"
              />
              <ul className="client-list" onMouseDown={(e) => e.stopPropagation()}>
                {filteredTagItems.map((tagItem) => (
                  <li
                    className={CSS_CLASSES.CURSOR}
                    key={tagItem._id}
                    onClick={
                      createTagSelectHandler
                        ? createTagSelectHandler({
                            tag: tagItem,
                            setTagEdit,
                            setTagIdEdit,
                            setTagQuery,
                            setTagEditDropdownOpen,
                          })
                        : () => {
                            if (setTagEdit) setTagEdit(tagItem.tag_name);
                            if (setTagIdEdit) setTagIdEdit(tagItem._id);
                            if (setTagQuery) setTagQuery("");
                            if (setTagEditDropdownOpen) setTagEditDropdownOpen(false);
                          }
                    }
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {tagItem.tag_name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <input type="hidden" name="tag" value={tagIdEdit || ''} />
      </div>
    );
  }

  // Render view mode or merged mode
  if (!isEditMode || isMergedMode) {
    const displayTag = isRowMode ? row?.tag : group?.tag;
    const handleClick = onClick || handleSectionClick;
    
    return (
      <div className={`tag-section-overview-outer ${className}`} style={style}>
        <div
          className="tag-section"
          onClick={handleClick}
        >
          <p
            className={`${CSS_CLASSES.CURSOR} ${displayTag ? CSS_CLASSES.TAG_AVAILABLE : CSS_CLASSES.TAG_NOT_AVAILABLE}`}
            onClick={(e) => {
              e.stopPropagation();
              if (handleClick) {
                handleClick();
              }
            }}
          >
            {viewModeOverflow && (
              <span className="tooltip-tracker">
                {" "}
                {displayTag ? (
                  displayTag.tag_name
                ) : (
                  <TagIcon />
                )}
              </span>
            )}
            <span
              ref={viewModeTagRef}
              style={INLINE_STYLES.TAG_ELLIPSIS_NO_MAX_WIDTH}
            >
              {" "}
              {displayTag ? (
                displayTag.tag_name
              ) : (
                <TagIcon />
              )}
            </span>
          </p>
        </div>
        <input type="hidden" name="tag" value={tagIdEdit || ''} />
      </div>
    );
  }

  return null;
};

export default TagDropdownSection;
